import { useRef, useEffect, useCallback } from 'react';
import { createScene, createCamera, createRenderer, createControls, loadGLBModel, setupCameraForModel } from '@components/imageHandling/components/thumbnails/3dViewer/utils/threeJSConfig';

let threeModulesPromise = null;

const loadThreeJSModules = () => {
  if (!threeModulesPromise) {
    threeModulesPromise = Promise.all([
      import('three'),
      import('three/examples/jsm/loaders/GLTFLoader.js'),
      import('three/examples/jsm/controls/OrbitControls.js'),
    ]).then(([THREE, gltfModule, orbitModule]) => ({
      THREE,
      GLTFLoader: gltfModule.GLTFLoader,
      OrbitControls: orbitModule.OrbitControls,
    }));
  }
  return threeModulesPromise;
};

export const useThreeJS = (containerRef, modelUrl, C) => {
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const modelRef = useRef(null);
  const animationIdRef = useRef(null);
  const threeRef = useRef(null);

  const handleResize = useCallback(() => {
    if (!containerRef.current || !rendererRef.current || !cameraRef.current) return;

    const width = containerRef.current.offsetWidth || 300;
    const height = containerRef.current.offsetHeight || 300;

    cameraRef.current.aspect = width / height;
    cameraRef.current.updateProjectionMatrix();
    rendererRef.current.setSize(width, height);
  }, [containerRef]);

  const initializeThreeJS = useCallback(async () => {
    if (!containerRef.current) return false;

    try {
      const modules = await loadThreeJSModules();
      // The hook may have unmounted while the dynamic import was in flight.
      const container = containerRef.current;
      if (!container) return false;

      threeRef.current = modules;
      const { THREE, OrbitControls } = modules;

      const width = container.offsetWidth || 300;
      const height = container.offsetHeight || 300;

      const scene = createScene(THREE);
      const camera = createCamera(THREE, width, height);
      const renderer = createRenderer(THREE, width, height, C);
      const controls = createControls(OrbitControls, camera, renderer.domElement);

      container.appendChild(renderer.domElement);

      sceneRef.current = scene;
      cameraRef.current = camera;
      rendererRef.current = renderer;
      controlsRef.current = controls;

      const animate = () => {
        animationIdRef.current = requestAnimationFrame(animate);
        controls.update();
        renderer.render(scene, camera);
      };
      animate();

      return true;
    } catch (error) {
      console.error('[useThreeJS] Failed to initialize Three.js:', error);
      return false;
    }
  }, [containerRef]);

  const loadModel = useCallback((url) => {
    if (!url || !threeRef.current || !sceneRef.current || !cameraRef.current || !controlsRef.current) {
      return;
    }

    const { THREE, GLTFLoader } = threeRef.current;

    loadGLBModel(
      GLTFLoader,
      url,
      (gltf) => {
        if (modelRef.current) {
          sceneRef.current.remove(modelRef.current);
        }

        const model = gltf.scene;
        modelRef.current = model;
        sceneRef.current?.add(model);

        setupCameraForModel(THREE, cameraRef.current, model, controlsRef.current);
      },
      (error) => {
        console.error('[useThreeJS] Model loading error:', error);
      }
    );
  }, []);

  const cleanup = useCallback(() => {
    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
    }

    if (rendererRef.current && containerRef.current && containerRef.current.contains(rendererRef.current.domElement)) {
      containerRef.current.removeChild(rendererRef.current.domElement);
      rendererRef.current.dispose();
    }

    sceneRef.current = null;
    rendererRef.current = null;
    cameraRef.current = null;
    controlsRef.current = null;
    modelRef.current = null;
  }, [containerRef]);

  useEffect(() => {
    initializeThreeJS();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      cleanup();
    };
  }, [initializeThreeJS, handleResize, cleanup]);

  useEffect(() => {
    if (sceneRef.current && modelUrl) {
      loadModel(modelUrl);
    }
  }, [modelUrl, loadModel, !!sceneRef.current]);

  return {
    isReady: !!sceneRef.current,
  };
};
